import React, { useState } from 'react';
import {
  GraduationCap, User, Zap, Activity, Trophy, Users, ExternalLink,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

// Content sourced from edzoleszek.hu (European Fitness Institute) — the
// official curriculum for the "Testépítés – Fitnesz Sportedző" qualification.
export const QUALIFICATION_COMPETENCIES = [
  { icon: User,     title: 'Személyi edzés vezetője',   description: 'Az egyéni bánásmód edzéselvén történő fitnesz személyi edzés szakképzett irányítója — sportolóknak és rekreációs edzést folytatóknak egyaránt.' },
  { icon: Activity, title: 'Funkcionális edző',          description: 'Felmér és korrekciót nyújt az erő, állóképesség és koordináció fejlesztésében a helyes mozgásminták kialakításával.' },
  { icon: Zap,      title: 'Erőnléti edző',              description: 'A különböző kondicionális képességek fejlesztése, akár más sportágakat is segítve a sportteljesítmény növelésében.' },
  { icon: Users,    title: 'Csoportos edzések vezetője', description: 'Speciális kondicionális fejlesztő óratípusok tartása egyéni sportolóknak, célcsoportoknak (pl. hölgyek) vagy sportcsapatoknak.' },
  { icon: Trophy,   title: 'Versenyfelkészítő edző',     description: 'Felkészítés klasszikus testépítésre, Men\u2019s Physique-re, Bikini fitneszre, Fitness Modell-re, testépítő versenyekre vagy erő-állóképességi sportokra (pl. CrossFit).' },
];

export const QUALIFICATION_CURRICULUM = [
  'Egyénre szabott komplex edzéstervezés · Terhelésélettan',
  'Fittségi tesztelések, állapotfelmérések rendszere',
  'Sportsérülések és megelőzésük · Dopping ismeretek',
  'A táplálkozás tudománya és élettana · Sporttáplálkozás',
  'Speciális edzésmódszerek: női test- és alakformálás, idősek edzése, férfi testépítés',
  'Alap és izolációs gyakorlatok · Sajáttestsúlyos, szabadsúlyzós és gépes edzések',
  'Funkcionális és erőnléti tréningek',
  'Edzésrendszerek felépítése és szabályai a fitnesz sportban',
  'Izomélettani ismeretek, anatómiai alapfogalmak, kondicionális képességek fejlesztése',
  'Edzéselmélet · Sportélettan · Sportanatómia (az izmok funkcionális működése)',
  'Egészségtan · Gimnasztika · Sportpedagógia · Sportpszichológia',
];

/**
 * Standalone qualification modal component. Renders both the inline
 * trigger (a highlighted underlined button with a graduation-cap icon)
 * AND the modal it opens. Pass `label` to control the trigger's text
 * (defaults to "Testépítés – Fitnesz Sportedző").
 *
 * Used on: About section (RÓLAM), and the two dedicated SEO landings
 * (`/szemelyi-edzo-miskolc`, `/online-szemelyi-edzo`).
 */
const QualificationDialog = ({
  label = 'Testépítés - Fitnesz Sportedző',
  testId = 'qualification-trigger',
  triggerClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        aria-haspopup="dialog"
        className={`group inline-flex items-baseline gap-1 font-semibold text-yellow-700 border-b-2 border-yellow-600/60 hover:text-yellow-800 hover:border-yellow-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600/50 focus-visible:ring-offset-2 rounded-sm transition-colors cursor-pointer ${triggerClassName}`}
        title="Kattints a részletes kompetenciákért"
      >
        <em className="not-italic">{label}</em>
        <GraduationCap className="w-4 h-4 self-center opacity-80 group-hover:opacity-100" strokeWidth={2.5} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="qualification-dialog"
          className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 bg-white"
        >
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 pr-12 rounded-t-lg">
            <div className="flex items-start gap-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-600/20 border border-yellow-600/30 rounded-lg shrink-0">
                <GraduationCap className="text-yellow-400" size={26} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-yellow-400 uppercase tracking-widest mb-1 leading-snug break-words">
                  EKKR-MKKR 5. szintű szakmai képesítés
                </div>
                <DialogHeader>
                  <DialogTitle
                    className="text-2xl font-bold leading-tight"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Testépítés – Fitnesz Sportedző
                  </DialogTitle>
                  <DialogDescription className="text-gray-300 text-sm mt-1">
                    Államilag és nemzetközileg elismert szakképesítés Europass-tanúsítvánnyal.
                    Vizsgaközpont: Magyar Testnevelési és Sporttudományi Egyetem.
                  </DialogDescription>
                </DialogHeader>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Mi a különbség a személyi edző és a sportedző között? */}
            <div
              data-testid="qualification-intro"
              className="rounded-xl border border-yellow-600/30 bg-gradient-to-br from-yellow-50 to-white p-4 md:p-5"
            >
              <h4 className="text-base font-bold text-gray-900 mb-2 leading-snug break-words" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Mi a különbség a személyi edző és a sportedző között?
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed break-words">
                A „személyi edző” elnevezés mögött sokféle képzés állhat, a néhány hetes tanfolyamtól a hosszabb
                képzésekig. A sportedzői szakképesítés <span className="font-semibold text-gray-900">államilag elismert</span>,
                a vizsgát a Testnevelési Egyetem vizsgaközpontjában tettem le, és Europass-tanúsítvánnyal egész
                Európában elfogadott. Számodra ez annyit jelent, hogy a tervedet nem megérzésre vagy divatos trendek
                alapján állítom össze, hanem azon a tudáson, ami bizonyítottan működik, és biztonságos számodra.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Miben tudok segíteni
              </h4>
              <ul className="space-y-3" data-testid="qualification-competencies">
                {QUALIFICATION_COMPETENCIES.map((comp, idx) => {
                  const Icon = comp.icon;
                  return (
                    <li
                      key={idx}
                      data-testid={`qualification-competency-${idx}`}
                      className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-yellow-600/50 hover:bg-yellow-50/40 transition-colors"
                    >
                      <div className="inline-flex items-center justify-center w-9 h-9 bg-yellow-100 rounded-lg shrink-0">
                        <Icon className="text-yellow-700" size={18} />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm leading-snug">{comp.title}</div>
                        <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{comp.description}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="border-t border-gray-200 pt-5">
              <h4 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                A képzésben elsajátított főbb szakterületek:
              </h4>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-700">
                {QUALIFICATION_CURRICULUM.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 leading-relaxed">
                    <span className="text-yellow-600 shrink-0 font-bold">·</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-gray-200 pt-4 text-[11px] text-gray-500 leading-relaxed">
              Forrás:{' '}
              <a
                href="https://www.edzoleszek.hu/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-0.5 text-yellow-700 hover:text-yellow-800 underline decoration-yellow-600/40"
                data-testid="qualification-source-link"
              >
                edzoleszek.hu — European Fitness Institute
                <ExternalLink className="w-3 h-3" />
              </a>{' '}
              hivatalos képesítési tájékoztatója.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QualificationDialog;
